import {
  BUSINESS_TYPE_DEFINITIONS,
  CARE_TYPE_DISPLAY_TO_BUSINESS_TYPE_KEY,
  PROVIDER_TYPES,
  mapCareTypeToBusinessTypeKey,
} from "../generated/careTaxonomy";

/** All API-accepted business_type slugs from taxonomy. */
export const VALID_SEARCH_BUSINESS_TYPE_SLUGS = new Set<string>(
  BUSINESS_TYPE_DEFINITIONS.map((definition) => definition.key)
);

/** Free-text social-worker case types → API business_type slugs. */
const CASE_TYPE_ALIASES: Record<string, string> = {
  chronic_management: "chronic_disease_management",
  "chronic management": "chronic_disease_management",
  chronic_care: "chronic_disease_management",
  long_term_care: "skilled_nursing",
  "long-term care": "skilled_nursing",
  discharge_planning: "home_health",
  "discharge planning": "home_health",
  post_acute: "rehabilitation",
  "post-acute": "rehabilitation",
  post_acute_care: "rehabilitation",
  snf: "skilled_nursing",
  home_health_aide: "home_health",
};

/**
 * Datalist suggestions for free-text Case Type.
 * Prefer taxonomy searchFilterLabel / aliases that resolve via
 * resolveSearchBusinessTypeSlug (not inventing unrelated copy).
 */
export const CASE_TYPE_INPUT_SUGGESTIONS: readonly string[] = (() => {
  // Plan 34: only suggest labels that appear in the Provider Type search picklist.
  const searchPicklistLabels = new Set(PROVIDER_TYPES.map((option) => option.value));
  const labels = new Set<string>();
  for (const definition of BUSINESS_TYPE_DEFINITIONS) {
    if (definition.key === "other") {
      continue;
    }
    if (definition.status === "deprecated") {
      continue;
    }
    if (
      definition.searchFilterLabel &&
      searchPicklistLabels.has(definition.searchFilterLabel)
    ) {
      labels.add(definition.searchFilterLabel);
    }
  }
  labels.add("Discharge planning");
  return Array.from(labels).sort((a, b) => a.localeCompare(b));
})();

function normalizeCaseTypeKey(value: string): string {
  return value.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function isValidSearchBusinessTypeSlug(slug: string): boolean {
  return VALID_SEARCH_BUSINESS_TYPE_SLUGS.has(slug);
}

/**
 * Prefer replaced_by when a taxonomy key is temporarily deprecated for UI.
 * Plan 33: ``home_care`` is active again — this path does not rewrite it.
 * Affinity/suggest never follows replaced_by (see providerTypeServiceAffinity).
 */
function resolveActiveBusinessTypeSlug(slug: string): string {
  const definition = BUSINESS_TYPE_DEFINITIONS.find((entry) => entry.key === slug);
  if (
    definition?.status === "deprecated" &&
    definition.replacedBy &&
    isValidSearchBusinessTypeSlug(definition.replacedBy)
  ) {
    return definition.replacedBy;
  }
  return slug;
}

/** True when the case type is already a canonical taxonomy slug (not a display label). */
export function isCanonicalSearchBusinessTypeSlug(
  rawValue: string | null | undefined
): boolean {
  const trimmed = rawValue?.trim();
  if (!trimmed || /[\s-]/.test(trimmed)) {
    return false;
  }
  return isValidSearchBusinessTypeSlug(normalizeCaseTypeKey(trimmed));
}

/**
 * Resolve a free-text case type or care label to a valid search business_type slug.
 * Returns undefined when no safe mapping exists (caller should omit business_type).
 */
export function resolveSearchBusinessTypeSlug(
  rawValue: string | null | undefined
): string | undefined {
  const trimmed = rawValue?.trim();
  if (!trimmed) {
    return undefined;
  }

  const normalized = normalizeCaseTypeKey(trimmed);
  if (isValidSearchBusinessTypeSlug(normalized)) {
    return resolveActiveBusinessTypeSlug(normalized);
  }

  const alias =
    CASE_TYPE_ALIASES[normalized] ?? CASE_TYPE_ALIASES[trimmed.toLowerCase()];
  if (alias && isValidSearchBusinessTypeSlug(alias)) {
    return resolveActiveBusinessTypeSlug(alias);
  }

  const mapped = mapCareTypeToBusinessTypeKey(trimmed);
  if (isValidSearchBusinessTypeSlug(mapped)) {
    return resolveActiveBusinessTypeSlug(mapped);
  }

  const lower = trimmed.toLowerCase();
  for (const [label, key] of Object.entries(
    CARE_TYPE_DISPLAY_TO_BUSINESS_TYPE_KEY
  )) {
    if (label.toLowerCase() === lower && isValidSearchBusinessTypeSlug(key)) {
      return resolveActiveBusinessTypeSlug(key);
    }
  }

  // Search-filter labels come straight from the generated registry (e.g.
  // "Home Care Agency" / "Home Health Agency" after the home-care split), so
  // future taxonomy renames resolve without hand-maintained alias tables.
  const byTaxonomyLabel = BUSINESS_TYPE_DEFINITIONS.find(
    (definition) =>
      definition.searchFilterLabel?.trim().toLowerCase() === lower ||
      definition.fullLabel.trim().toLowerCase() === lower
  );
  if (byTaxonomyLabel) {
    return resolveActiveBusinessTypeSlug(byTaxonomyLabel.key);
  }

  if (normalized.includes("chronic")) {
    return "chronic_disease_management";
  }
  if (normalized.includes("hospice") || normalized.includes("end_of_life")) {
    return "hospice";
  }
  if (normalized.includes("memory") || normalized.includes("dementia")) {
    return "memory_care";
  }
  if (normalized.includes("rehab")) {
    return "rehabilitation";
  }
  if (normalized.includes("discharge")) {
    return "home_health";
  }
  if (normalized.includes("wound")) {
    return "wound_care";
  }
  if (
    normalized.includes("post_surgery") ||
    normalized.includes("post_surgical")
  ) {
    return "post_surgery_care";
  }

  return undefined;
}

/** @deprecated Use resolveSearchBusinessTypeSlug */
export const resolveCaseTypeToSearchBusinessType = resolveSearchBusinessTypeSlug;
